/**
 * The account mutation command and the rules that read it, with no dependency of any kind.
 *
 * A host decides what a mutation means to the instances it runs long before it can touch a
 * credential, and a browser is such a host. This module therefore imports nothing -- no Node
 * built-in, no package, not even another module of this package -- so that it can be bundled
 * for a browser through the `@modelprofile.com/authswitch/mutation` entry point. Everything
 * that reads a credential stays behind the main entry point, which is Node-only.
 */

export type TAuthSwitchMutation = { harnessId: string } & (
  | { action: 'save'; accountId?: string; keepActive: boolean }
  | { action: 'switch' | 'remove'; accountId: string }
);

/** Validate the secret-free command boundary, including callers that do not use TypeScript. */
export const assertAuthSwitchMutation: (value: unknown) => asserts value is TAuthSwitchMutation = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('Invalid account operation.');
  const item = value as Record<string, unknown>;
  const keys = item.action === 'save' ? ['harnessId', 'action', 'accountId', 'keepActive'] : ['harnessId', 'action', 'accountId'];
  if (Object.keys(item).some(key => !keys.includes(key))
    || typeof item.harnessId !== 'string' || !/^[a-z][a-z0-9-]{0,63}$/.test(item.harnessId)
    || !['save', 'switch', 'remove'].includes(String(item.action))
    || (item.accountId !== undefined && (typeof item.accountId !== 'string' || !item.accountId.length || item.accountId.length > 512))
    || (item.action === 'save' ? typeof item.keepActive !== 'boolean' : typeof item.accountId !== 'string')) {
    throw new Error('Invalid account operation.');
  }
};

/** Whether a mutation replaces the harness's active login, and therefore concerns running instances. */
export const authSwitchMutationReplacesLogin = (mutationArg: TAuthSwitchMutation): boolean =>
  mutationArg.action === 'switch' || (mutationArg.action === 'save' && !mutationArg.keepActive);
